import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { useSelector } from 'react-redux';
import { jsPDF } from 'jspdf';

// Import invitation PDF images
import invitationImage1 from '../../../../assets/images/invitationpdf/Your Invitation to the Billionaires for Peace Conclave as a VIP I Am Peacekeeper Partner_1.jpg';
import invitationImage2 from '../../../../assets/images/invitationpdf/Your Invitation to the Billionaires for Peace Conclave as a VIP I Am Peacekeeper Partner_2.jpg';
import invitationImage3 from '../../../../assets/images/invitationpdf/Your Invitation to the Billionaires for Peace Conclave as a VIP I Am Peacekeeper Partner_3.jpg';

const BPF = () => {
  const { user } = useSelector((state) => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [currentPage, setCurrentPage] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState('');
  
  // Invitation PDF images from assets folder
  const invitationImages = [invitationImage1, invitationImage2, invitationImage3];
  const totalPages = invitationImages.length;

  const nextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Render current page image
  const renderPage = () => {
    const currentImage = invitationImages[currentPage];
    
    return (
      <Box 
        sx={{ 
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center'
        }}
      >
        <img 
          src={currentImage} 
          alt={`Invitation Page ${currentPage + 1}`}
          style={{
            maxWidth: '100%',
            height: 'auto',
            maxHeight: '80vh',
            objectFit: 'contain'
          }}
        />
      </Box>
    );
  };

  // Helper function to detect iOS devices
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // Helper function to detect Safari browser
  const isSafari = () => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  };

  // iOS-specific PDF download handler - Force save to device
  const handleIOSPDFDownload = (blob, filename) => {
    try {
      console.log('Handling iOS PDF download for BPF:', filename);
      setDownloadStatus('Preparing PDF download for iOS...');

      // For iOS PDFs, we need to force save to device, not open in browser
      // Try multiple approaches in sequence
      
      // Approach 1: Try Web Share API first (most reliable for iOS)
      if (navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], filename, { type: 'application/pdf' });
          if (navigator.canShare({ files: [file] })) {
            navigator.share({
              files: [file],
              title: 'BPF Invitation PDF',
              text: `Save your BPF invitation PDF: ${filename}`
            }).then(() => {
              setDownloadStatus('PDF shared successfully! Choose "Save to Files" to save to your device.');
            }).catch((shareError) => {
              console.log('Web Share failed, trying alternative method:', shareError);
              handleIOSPDFFallback(blob, filename);
            });
            return;
          }
        } catch (shareError) {
          console.log('Web Share API error, trying fallback:', shareError);
          handleIOSPDFFallback(blob, filename);
          return;
        }
      }

      // If Web Share is not available, use fallback
      handleIOSPDFFallback(blob, filename);

    } catch (error) {
      console.error('iOS PDF download failed:', error);
      handleIOSPDFFallback(blob, filename);
    }
  };

  // iOS PDF fallback method - More aggressive approach
  const handleIOSPDFFallback = (blob, filename) => {
    try {
      console.log('Using iOS PDF fallback method for BPF:', filename);
      setDownloadStatus('Preparing PDF for device save...');

      // Create a new blob with explicit PDF MIME type
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      
      // Try to use Cordova file system if available
      if (window.cordova && window.cordova.file) {
        console.log('Using Cordova file system for PDF save...');
        setDownloadStatus('Saving PDF to device using Cordova...');
        
        // Note: saveFileToDevice would need to be implemented if using Cordova
        // For now, fall through to web fallback
        handleIOSPDFWebFallback(pdfBlob, filename);
        return;
      }

      // Web fallback for non-Cordova environments
      handleIOSPDFWebFallback(pdfBlob, filename);

    } catch (error) {
      console.error('iOS PDF fallback failed:', error);
      setDownloadStatus('PDF save failed. Please try the Web Share option.');
    }
  };

  // iOS PDF web fallback - Force download without opening
  const handleIOSPDFWebFallback = (pdfBlob, filename) => {
    try {
      console.log('Using iOS PDF web fallback for BPF:', filename);
      setDownloadStatus('Preparing PDF download...');

      // Create a data URL instead of blob URL to force download
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        
        // Create a temporary link element with aggressive download settings
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        link.style.display = 'none';
        
        // Add attributes to force download
        link.setAttribute('download', filename);
        link.setAttribute('type', 'application/pdf');
        link.setAttribute('rel', 'noopener');
        
        // Add to DOM
        document.body.appendChild(link);
        
        // Try multiple click approaches
        try {
          // Method 1: Direct click
          link.click();
          
          // Method 2: Synthetic click event
          setTimeout(() => {
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            link.dispatchEvent(clickEvent);
          }, 50);
          
          // Method 3: Programmatic click as last resort
          setTimeout(() => {
            if (link.click) {
              link.click();
            }
          }, 100);
          
          // Method 4: Try iframe approach for iOS (more aggressive)
          setTimeout(() => {
            try {
              const iframe = document.createElement('iframe');
              iframe.style.display = 'none';
              iframe.src = dataUrl;
              document.body.appendChild(iframe);
              
              setTimeout(() => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                }
              }, 1000);
            } catch (iframeError) {
              console.log('Iframe method failed:', iframeError);
            }
          }, 200);
          
        } catch (clickError) {
          console.log('Click methods failed:', clickError);
        }
        
        // Clean up
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
        }, 2000);

        setDownloadStatus('PDF download initiated. Check your Downloads or Files app.');
        
        // Show instructions for manual saving if needed
        setTimeout(() => {
          setDownloadStatus('If PDF opened in browser, long press the PDF and select "Save to Files" or "Add to Files".');
        }, 2000);
      };
      
      reader.onerror = (error) => {
        console.error('Error reading PDF blob:', error);
        setDownloadStatus('Failed to prepare PDF for download. Please try again.');
      };
      
      reader.readAsDataURL(pdfBlob);

    } catch (error) {
      console.error('iOS PDF web fallback failed:', error);
      setDownloadStatus('PDF download failed. Please try the Web Share option above.');
    }
  };

  // Download blob with mobile support (similar to VisitingCard)
  const downloadBlob = (blob, filename) => {
    try {
      console.log('Starting download for BPF:', filename, 'Blob size:', blob.size);

      // Enhanced mobile download handling
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isIOSDevice = isIOS();
      const isSafariBrowser = isSafari();
      
      console.log('Device type - Mobile:', isMobile, 'iOS:', isIOSDevice, 'Safari:', isSafariBrowser);

      // Handle iOS devices specifically
      if (isIOSDevice) {
        console.log('iOS device detected, using iOS-specific download handler');
        
        // Use specific PDF handler for PDF files on iOS
        if (filename.toLowerCase().endsWith('.pdf')) {
          console.log('iOS PDF download detected, using specialized PDF handler');
          handleIOSPDFDownload(blob, filename);
        }
        return;
      }

      // Try Web Share API for mobile devices (for PDFs)
      if (isMobile && navigator.share && navigator.canShare) {
        console.log('Attempting Web Share API...');
        const fileType = filename.endsWith('.pdf') ? 'application/pdf' : 'image/png';
        const file = new File([blob], filename, { type: fileType });
        
        if (navigator.canShare({ files: [file] })) {
          const shareTitle = filename.endsWith('.pdf') ? 'BPF Invitation PDF' : 'BPF Invitation';
          navigator.share({
            files: [file],
            title: shareTitle,
            text: `Download your ${filename.endsWith('.pdf') ? 'BPF invitation PDF' : 'BPF invitation image'}`
          }).then(() => {
            setDownloadStatus('File shared successfully!');
          }).catch((shareError) => {
            if (shareError.name !== 'AbortError') {
              console.log('Web Share failed, falling back to download:', shareError);
              setDownloadStatus('Sharing failed, downloading instead...');
              fallbackDownload(blob, filename);
            } else {
              setDownloadStatus('');
            }
          });
          return;
        }
      }

      // Regular download for desktop or when other methods are not available
      console.log('Using fallback download method...');
      setDownloadStatus('Downloading file...');
      fallbackDownload(blob, filename);

    } catch (error) {
      console.error('Error:', error);
      setDownloadStatus('Download failed. Please try again.');
      // Ultimate fallback
      fallbackDownload(blob, filename);
    }
  };

  // Fallback download method
  const fallbackDownload = (blob, filename) => {
    try {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDownloadStatus('PDF downloaded successfully!');
      setTimeout(() => setDownloadStatus(''), 3000);
    } catch (error) {
      console.error('Error in fallbackDownload:', error);
      setDownloadStatus('Download failed. Please try again.');
    }
  };

  // Handle PDF download with mobile support
  const handleDownload = async () => {
    setDownloadStatus('Preparing PDF download...');

    try {
      // Fetch the PDF file
      const response = await fetch('/doc/bpf-document.pdf');
      if (!response.ok) {
        throw new Error('Failed to fetch PDF');
      }
      
      const blob = await response.blob();
      const filename = `BPF-Invitation-${user?.peacekeeper_id || 'document'}.pdf`;
      const file = new File([blob], filename, { type: 'application/pdf' });

      // Check if mobile device
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isIOSDevice = isIOS();

      // For mobile devices, use Web Share API or download blob
      if (isMobile || isIOSDevice) {
        // Try Web Share API first (allows saving to device)
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: 'BPF Invitation PDF',
              text: `Save your BPF invitation PDF: ${filename}`,
            });
            setDownloadStatus('PDF shared successfully! Choose "Save to Files" to save to your device.');
            setTimeout(() => setDownloadStatus(''), 5000);
            return;
          } catch (shareError) {
            if (shareError.name === 'AbortError') {
              setDownloadStatus('');
              return;
            }
            console.log('Web Share failed, trying blob download:', shareError);
          }
        }

        // Fallback: Download using blob URL
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        try {
          link.click();
          setDownloadStatus('PDF download initiated. Check your Downloads or Files app.');
          setTimeout(() => {
            setDownloadStatus('If PDF didn\'t download, try tapping the download button again.');
          }, 3000);
        } catch (clickError) {
          console.error('Download click failed:', clickError);
          // Try opening in new tab as last resort
          window.open(blobUrl, '_blank');
          setDownloadStatus('PDF opened in new tab. Long press and save on mobile.');
        }
        
        // Clean up
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          URL.revokeObjectURL(blobUrl);
        }, 3000);
        
        return;
      }

      // Desktop: Open PDF in new tab
      window.open('/doc/bpf-document.pdf', '_blank');
      setDownloadStatus('PDF opened in new tab.');
      setTimeout(() => setDownloadStatus(''), 3000);
      
    } catch (err) {
      console.error('Download error:', err);
      setDownloadStatus('Failed to download PDF. Please try again.');
      setTimeout(() => setDownloadStatus(''), 5000);
    }
  };

  // Handle share - open native sharing options with PDF
  const handleShare = async () => {
    setDownloadStatus('Preparing PDF for sharing...');

    try {
      // Fetch the PDF file with proper headers
      const response = await fetch('/doc/bpf-document.pdf', {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch PDF');
      }
      
      // Get the blob and ensure it has the correct type
      const blob = await response.blob();
      console.log('PDF blob created:', blob.size, 'bytes, original type:', blob.type);
      
      // Always create a new blob with explicit PDF type to ensure it's correct
      const pdfBlob = new Blob([blob], { 
        type: 'application/pdf' 
      });
      
      console.log('PDF blob after type fix:', pdfBlob.size, 'bytes, type:', pdfBlob.type);
      
      const filename = `BPF-Invitation-${user?.peacekeeper_id || 'document'}.pdf`;
      
      // Create File object with explicit type
      const file = new File([pdfBlob], filename, { 
        type: 'application/pdf',
        lastModified: Date.now()
      });

      console.log('PDF file created:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      });

      // Try Web Share API first (opens native sharing options)
      if (navigator.share && navigator.canShare) {
        try {
          // Check if file sharing is supported
          const shareData = {
            files: [file],
            title: 'BPF Invitation PDF',
            text: 'Check out this BPF invitation PDF',
          };
          
          const canShareFile = navigator.canShare(shareData);
          console.log('Can share file:', canShareFile, 'Share data:', shareData);
          
          if (canShareFile) {
            await navigator.share(shareData);
            setDownloadStatus('PDF shared successfully!');
            setTimeout(() => setDownloadStatus(''), 3000);
            return;
          } else {
            console.log('File sharing not supported by browser');
            throw new Error('File sharing not supported');
          }
        } catch (shareError) {
          if (shareError.name !== 'AbortError') {
            console.error('Web Share API error:', shareError);
            setDownloadStatus('File sharing not supported. PDF download available.');
            setTimeout(() => setDownloadStatus(''), 5000);
          } else {
            setDownloadStatus('');
            return;
          }
        }
      } else {
        console.log('Web Share API not available');
        setDownloadStatus('Sharing not available. Please use download instead.');
        setTimeout(() => setDownloadStatus(''), 5000);
      }
      
    } catch (err) {
      console.error('Share error:', err);
      setDownloadStatus('Failed to share PDF. Please try download instead.');
      setTimeout(() => setDownloadStatus(''), 5000);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Page Indicator */}
      {invitationImages.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 1,
          alignItems: 'center'
        }}>
          {/* Previous Button */}
          <Button
            variant="outlined"
            startIcon={<NavigateBeforeIcon />}
            onClick={prevPage}
            disabled={currentPage === 0}
            size="small"
            sx={{
              minWidth: 'auto',
            }}
          />
          
          {/* Page Dots */}
          {Array.from({ length: totalPages }, (_, i) => i).map((page) => (
            <Box
              key={page}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: currentPage === page ? '#3A8139' : '#e0e0e0',
                cursor: 'pointer'
              }}
              onClick={() => setCurrentPage(page)}
            />
          ))}
          
          {/* Next Button */}
          <Button
            variant="outlined"
            endIcon={<NavigateNextIcon />}
            onClick={nextPage}
            disabled={currentPage >= totalPages - 1}
            size="small"
            sx={{
              minWidth: 'auto',
            }}
          />
        </Box>
      )}

      {/* Status Message */}
      {downloadStatus && (
        <Box sx={{ 
          textAlign: 'center', 
          bgcolor: downloadStatus.includes('successfully') || downloadStatus.includes('Shared') ? 'success.light' : 'info.light',
          borderRadius: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            {downloadStatus}
          </Typography>
        </Box>
      )}

      {/* Invitation Images Content */}
      <Box 
        className="invitation-content"
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          width: '100%',
          minHeight: '560px'
        }}
      >
        {renderPage()}
      </Box>

      {/* Page Info */}
      {invitationImages.length > 0 && (
        <Box sx={{ 
          textAlign: 'center', 
          mt: 1,
          p: 1,
          bgcolor: 'grey.50',
          borderRadius: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            Page {currentPage + 1} of {totalPages}
          </Typography>
        </Box>
      )}

      {/* Action Buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        mt: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={handleShare}
            disabled={invitationImages.length === 0}
            sx={{
              borderColor: '#214F7D',
              color: '#214F7D',
              textTransform: 'none',
              '&:hover': { 
                borderColor: '#1a3f63',
                backgroundColor: 'rgba(33, 79, 125, 0.04)'
              }
            }}
          >
            Share
          </Button>
        
        <Button
          variant="contained"
          startIcon={<PictureAsPdfIcon />}
          onClick={handleDownload}
          disabled={invitationImages.length === 0}
          sx={{
            bgcolor: '#3A8139',
            color: 'white',
            textTransform: 'none',
            '&:hover': { bgcolor: '#2d652c' }
          }}
        >
          Download PDF
        </Button>
      </Box>
    </Box>
  );
};

export default BPF;
